import { useState } from 'react';
import { KeyRound, Loader2, Mail, MessageCircle, Send } from 'lucide-react';
import { api, type AccesoPortal, type EstadoPortal } from '@/lib/api';

/**
 * Activar el portal de un propietario y ver cómo le llegó el acceso.
 *
 * Al activar sale un correo de bienvenida y, por WhatsApp, un enlace para que
 * el propietario cree su contraseña. Nadie del equipo la ve. Si el WhatsApp no
 * sale, el enlace va en el correo. Ver server/src/services/owner-portal-acceso.ts.
 */

const fecha = (iso: string) =>
  new Date(iso).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

const WHATSAPP: Record<AccesoPortal['whatsapp']['estado'], { texto: string; ok: boolean }> = {
  enviado: { texto: 'Enviado', ok: true },
  sent: { texto: 'Enviado', ok: true },
  delivered: { texto: 'Entregado', ok: true },
  read: { texto: 'Leído', ok: true },
  failed: { texto: 'No se entregó', ok: false },
  sin_telefono: { texto: 'Sin móvil en la ficha', ok: false },
  baja: { texto: 'Pidió no recibir WhatsApp', ok: false },
  sin_plantilla: { texto: 'Plantilla sin aprobar por Meta', ok: false },
  error: { texto: 'Error al enviar', ok: false },
};

export function ConfirmarActivacion({ ownerId, nombre, estado, onActivado }: {
  ownerId: string;
  nombre: string;
  estado: EstadoPortal;
  onActivado: () => Promise<void> | void;
}) {
  const [abierto, setAbierto] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const email = estado.destino?.email;
  const telefono = estado.destino?.telefono;

  async function activar() {
    setEnviando(true);
    setError(null);
    try {
      await api.enablePortal(ownerId);
      setAbierto(false);
    } catch (err: any) {
      setError(err.message || 'Error al activar el portal');
    } finally {
      setEnviando(false);
      // Aunque el envío falle, el acceso puede haber quedado activo: se vuelve a leer
      await onActivado();
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-sereno-dark/40">El acceso al portal no está activado para este propietario.</p>
      {!abierto ? (
        <button onClick={() => setAbierto(true)} disabled={!email} className="btn-secondary text-sm flex items-center gap-2">
          <KeyRound size={14} />
          Activar portal
        </button>
      ) : (
        <div className="rounded-xl border border-gray-200 p-4 space-y-3">
          <p className="text-sm font-semibold">¿Activar el portal de {nombre}?</p>
          <p className="text-sm text-sereno-dark/60">Se le enviará:</p>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between gap-3">
              <span className="text-sereno-dark/50 flex items-center gap-1.5"><Mail size={13} /> Correo de bienvenida</span>
              <span className="font-mono text-xs truncate">{email}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-sereno-dark/50 flex items-center gap-1.5"><MessageCircle size={13} /> Enlace para crear su contraseña</span>
              <span className="font-mono text-xs">{telefono ? `WhatsApp ${telefono}` : 'en el correo'}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-sereno-dark/50">Usuario</span>
              <span className="font-mono text-xs truncate">{email}</span>
            </div>
          </div>
          {!telefono && (
            <p className="text-xs text-amber-600">Sin móvil en la ficha: el enlace para crear la contraseña irá dentro del correo.</p>
          )}
          <p className="text-xs text-sereno-dark/40">El enlace sirve una vez y caduca en 72 horas. Nadie del equipo verá su contraseña.</p>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex gap-2">
            <button onClick={activar} disabled={enviando} className="btn-primary text-sm flex items-center gap-2">
              {enviando ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Activar y enviar
            </button>
            <button onClick={() => setAbierto(false)} disabled={enviando} className="btn-ghost text-sm">Cancelar</button>
          </div>
        </div>
      )}
      {!email && <p className="text-xs text-amber-600">Configura un email para poder activar el portal.</p>}
    </div>
  );
}

export function EstadoAcceso({ ownerId, estado, onCambio }: {
  ownerId: string;
  estado: EstadoPortal;
  onCambio: () => Promise<void> | void;
}) {
  const [enviando, setEnviando] = useState<'enlace' | 'bienvenida' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const a = estado.acceso;
  const pendiente = estado.enlacePendiente;
  const caducado = pendiente ? new Date(pendiente.caduca).getTime() <= Date.now() : false;

  async function reenviar(bienvenida: boolean) {
    setEnviando(bienvenida ? 'bienvenida' : 'enlace');
    setError(null);
    try {
      await api.sendPortalAccess(ownerId, bienvenida);
    } catch (err: any) {
      setError(err.message || 'No se pudo enviar');
    } finally {
      setEnviando(null);
      await onCambio();
    }
  }

  if (!estado.enabled) return null;
  const wa = a ? WHATSAPP[a.whatsapp.estado] : null;

  return (
    <div className="space-y-2 border-t border-gray-100 pt-3">
      {pendiente?.proposito === 'activation' && (
        <p className={`text-xs ${caducado ? 'text-amber-600' : 'text-sereno-dark/50'}`}>
          {caducado
            ? 'No llegó a crear su contraseña y el enlace ha caducado: reenvíaselo.'
            : `Aún no ha creado su contraseña. El enlace caduca el ${fecha(pendiente.caduca)}.`}
        </p>
      )}
      {a && (
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between gap-3">
            <span className="text-sereno-dark/50">WhatsApp con el enlace</span>
            <span className={`text-xs text-right ${wa?.ok ? 'text-green-600' : 'text-amber-600'}`} title={a.whatsapp.detalle}>
              {wa?.texto}{a.whatsapp.telefono ? ` · ${a.whatsapp.telefono}` : ''}
            </span>
          </div>
          {a.correo && (
            <div className="flex justify-between gap-3">
              <span className="text-sereno-dark/50">{a.correo.conEnlace ? 'Correo con el enlace' : 'Correo de bienvenida'}</span>
              <span className={`text-xs text-right ${a.correo.estado === 'enviado' ? 'text-green-600' : 'text-amber-600'}`} title={a.correo.detalle}>
                {a.correo.estado === 'enviado' ? 'Enviado' : a.correo.estado === 'no_configurado' ? 'Correo sin configurar' : 'Error al enviar'} · {a.correo.para}
              </span>
            </div>
          )}
          <p className="text-xs text-sereno-dark/40">Enviado el {fecha(a.en)} por {a.por}</p>
        </div>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => reenviar(false)} disabled={!!enviando} className="btn-ghost border border-gray-200 text-xs px-2.5 py-1.5 flex items-center gap-1.5"
          title="Manda un enlace nuevo para crear la contraseña; el anterior deja de valer">
          {enviando === 'enlace' ? <Loader2 size={12} className="animate-spin" /> : <MessageCircle size={12} />}
          Reenviar enlace
        </button>
        <button onClick={() => reenviar(true)} disabled={!!enviando} className="btn-ghost border border-gray-200 text-xs px-2.5 py-1.5 flex items-center gap-1.5"
          title="El correo de bienvenida y un enlace nuevo">
          {enviando === 'bienvenida' ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
          Reenviar bienvenida
        </button>
      </div>
    </div>
  );
}
